/**
 * @module public/js/core/auth/pendingLoginIssue
 * @summary Session-storage helpers for one-shot login issues.
 */

const PENDING_LOGIN_ISSUE_KEY = 'topsail.pendingLoginIssue';

/**
 * Resolve the session storage implementation when one is available.
 *
 * @param {Storage|null|undefined} [storage] - Optional storage override.
 * @returns {Storage|null} Session storage implementation or null.
 */
function resolveStorage(storage) {
  if (storage) {
    return storage;
  }

  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

/**
 * Persist a one-shot login issue for the next login-page render.
 *
 * @param {string} message - Human-friendly login issue message.
 * @param {Storage|null|undefined} [storage] - Optional storage override.
 * @returns {void}
 */
export function persistPendingLoginIssue(message, storage) {
  const target = resolveStorage(storage);
  const normalized = String(message || '').trim();
  if (!target || !normalized) {
    return;
  }

  target.setItem(PENDING_LOGIN_ISSUE_KEY, normalized);
}

/**
 * Clear any stored one-shot login issue.
 *
 * @param {Storage|null|undefined} [storage] - Optional storage override.
 * @returns {void}
 */
export function clearPendingLoginIssue(storage) {
  const target = resolveStorage(storage);
  if (!target) {
    return;
  }

  target.removeItem(PENDING_LOGIN_ISSUE_KEY);
}

/**
 * Read and clear the pending one-shot login issue.
 *
 * @param {Storage|null|undefined} [storage] - Optional storage override.
 * @returns {string|null} Pending issue message or null when none exists.
 */
export function consumePendingLoginIssue(storage) {
  const target = resolveStorage(storage);
  if (!target) {
    return null;
  }

  const message = String(target.getItem(PENDING_LOGIN_ISSUE_KEY) || '').trim();
  target.removeItem(PENDING_LOGIN_ISSUE_KEY);
  return message || null;
}
